import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { firstValueFrom, map, timeout, type Observable } from 'rxjs';
import { API_BASE_URL } from '@shared/http';
import {
  PointsHistorySchema,
  RedeemInfoSchema,
  RedemptionListSchema,
  RedemptionSchema,
  type PointsHistory,
  type RedeemInfo,
  type Redemption,
  type RedemptionList,
} from '../model/points.schema';

const REDEEM_TIMEOUT_MS = 30_000;

export interface RedeemRequest {
  points: number;
  network: string;
  idempotencyKey: string;
}

@Injectable({ providedIn: 'root' })
export class PointsApi {
  private readonly base = inject(API_BASE_URL);
  private readonly http = inject(HttpClient);

  history$(cursor?: string, limit = 20): Observable<PointsHistory> {
    let params = new HttpParams().set('limit', limit);
    if (cursor) params = params.set('cursor', cursor);
    return this.http
      .get<unknown>(`${this.base}/me/points/history`, { params })
      .pipe(map(raw => PointsHistorySchema.parse(raw)));
  }

  redemptions$(limit = 20, offset = 0): Observable<RedemptionList> {
    const params = new HttpParams().set('limit', limit).set('offset', offset);
    return this.http
      .get<unknown>(`${this.base}/me/points/redemptions`, { params })
      .pipe(map(raw => RedemptionListSchema.parse(raw)));
  }

  redeemInfo$(): Observable<RedeemInfo> {
    return this.http
      .get<unknown>(`${this.base}/me/points/redeem-info`)
      .pipe(map(raw => RedeemInfoSchema.parse(raw)));
  }

  async redeem(request: RedeemRequest, freshToken: string): Promise<Redemption> {
    const raw = await firstValueFrom(
      this.http
        .post<unknown>(`${this.base}/me/points/redeem-usdc`, request, {
          headers: new HttpHeaders({ 'x-fresh-token': freshToken }),
        })
        .pipe(timeout(REDEEM_TIMEOUT_MS)),
    );
    return RedemptionSchema.parse(raw);
  }
}
